import { Card, ColumnHelp, MetricCard, MetricGrid } from "@nymbl/nos2-rules";
import { useSignOffs } from "@/hooks/use-time-signoffs";
import { INCENTIVE_LATE_TILE_HELP } from "@/lib/incentives/incentive-help";
import {
  awardBreakdown,
  type BreakdownRow,
  finalAward,
  formatAwardPercent,
  formulaLine,
  lateTileText,
} from "@/lib/incentives/incentives";
import { lateSignOffCount } from "@/lib/time/signoff-rules";
import { cn } from "@/lib/utils";
import type { IncentiveYear } from "@/schemas/incentives";

function rowValue(row: BreakdownRow): string {
  const text = formatAwardPercent(row.value);
  return row.kind === "penalty" && row.value > 0 ? `−${text}` : text;
}

/**
 * Four metric tiles (final award with its formula, earned award, reductions, late sign-offs),
 * then the breakdown: company, individual, earned award, each reduction as stored, final award.
 * Reductions only when above 0; late sign-offs only for the current year, which is what the
 * sign-offs data covers, and only once they have loaded.
 */
export function AwardSummary({ year }: { year: IncentiveYear }) {
  const award = finalAward(year);
  const rows = awardBreakdown(year);
  const isCurrentYear = year.year === new Date().getFullYear();
  const signOffs = useSignOffs();
  const lateCount =
    isCurrentYear && signOffs.data ? lateSignOffCount(signOffs.data.rows, year.year) : null;

  return (
    <div className="flex flex-col gap-5">
      <section aria-label={`Award summary, ${year.year}`}>
        <MetricGrid>
          <div data-testid="incentive-headline">
            <MetricCard
              label="Final award"
              value={formatAwardPercent(award.final)}
              detail={formulaLine(award)}
            />
          </div>
          <div data-testid="incentive-earned">
            <MetricCard label="Earned award" value={formatAwardPercent(award.earned)} />
          </div>
          {award.reductions > 0 && (
            <div data-testid="incentive-reductions">
              <MetricCard
                label="Reductions"
                value={`−${formatAwardPercent(award.reductions)}`}
                alert
                alertLabel="Reduced"
              />
            </div>
          )}
          {lateCount !== null && (
            <div data-testid="incentive-late">
              <MetricCard
                label="Late submissions"
                value={lateTileText(lateCount)}
                help={INCENTIVE_LATE_TILE_HELP}
              />
            </div>
          )}
        </MetricGrid>
      </section>
      <Card className="gap-0 px-5 py-3">
        <ul aria-label={`Award breakdown, ${year.year}`} className="divide-y">
          {rows.map((row) => (
            <li
              key={row.key}
              className={cn(
                "flex min-h-11 items-center justify-between gap-3 py-1 text-sm",
                (row.kind === "subtotal" || row.kind === "total") && "font-semibold",
                row.kind === "total" && "text-base",
              )}
            >
              <span className="flex items-center gap-1">
                {row.label}
                <ColumnHelp name={row.label} text={row.help} />
              </span>
              <span
                className={cn(
                  "tabular-nums",
                  row.kind === "penalty" && row.value > 0 && "text-destructive",
                )}
              >
                {rowValue(row)}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
